/**
 * Passages and the case reader.
 *
 * Tools show short passages from library authorities (precedent hits, research
 * grounding, chat source excerpts). A passage links to `/sources/:id?passage=…`
 * and the reader locates it in the full text to highlight and scroll to it. The
 * library's chunks are whitespace-normalised copies of the source text, so the
 * match is on words with any whitespace between them — never on raw offsets.
 */

/** Words of a passage kept in a link: enough to find it, short enough for a URL. */
export const LINK_WORDS = 30;
/** Words used to anchor the start of a passage in the full text. */
const ANCHOR_WORDS = 12;
/** A trimmed excerpt can start mid-word; try anchoring from this many leading words in. */
const ANCHOR_ATTEMPTS = 3;

/** A passage without the ellipses excerpting adds, on one line. */
export function cleanPassage(passage: string): string {
  return passage
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^(…|\.\.\.)\s*/, '')
    .replace(/\s*(…|\.\.\.)$/, '')
    .trim();
}

/**
 * Which key opens an authority in the reader: its `authorityKey` (`uplaw:<did>` in the law library —
 * ADR 0011 — or a local id), else its legacy local `sourceId`; null when there's nothing to open.
 */
export function authorityKeyOf(a: { authorityKey?: string | null; sourceId?: string | null }): string | null {
  return a.authorityKey || a.sourceId || null;
}

/** Whether a key names a law-library (UpLaw) authority rather than a local one. */
export function isLawLibraryKey(key: string | null | undefined): boolean {
  return !!key && key.toLowerCase().startsWith('uplaw:');
}

/**
 * The case-reader link for an authority — by key (`uplaw:<did>` or a local id) — optionally
 * pointing at one passage.
 */
export function sourceHref(sourceId: string, passage?: string | null): string {
  const base = `/sources/${sourceId}`;
  if (!passage) return base;
  const words = cleanPassage(passage).split(' ').filter(Boolean);
  if (words.length === 0) return base;
  const params = new URLSearchParams({ passage: words.slice(0, LINK_WORDS).join(' ') });
  if (words.length > LINK_WORDS) params.set('words', String(words.length));
  return `${base}?${params.toString()}`;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Where `passage` sits in `text`, as a half-open character range, or null when it
 * can't be found. `totalWords` extends the highlight past the words carried in a
 * (truncated) link to the passage's full length.
 */
export function locatePassage(text: string, passage: string, totalWords?: number): { start: number; end: number } | null {
  const words = cleanPassage(passage).split(' ').filter(Boolean);
  if (words.length === 0 || !text) return null;
  const wanted = Math.max(words.length, totalWords ?? 0);
  for (let skip = 0; skip < Math.min(ANCHOR_ATTEMPTS, words.length); skip++) {
    const anchor = words.slice(skip, skip + ANCHOR_WORDS);
    const pattern = new RegExp(anchor.map(escapeRegExp).join('\\s+'));
    const hit = pattern.exec(text);
    if (!hit) continue;
    // Walk forward from the anchor, consuming the rest of the passage's words.
    const word = /\S+/g;
    word.lastIndex = hit.index;
    let end = hit.index;
    for (let consumed = 0; consumed < wanted - skip; consumed++) {
      const next = word.exec(text);
      if (!next) break;
      end = next.index + next[0].length;
    }
    return { start: hit.index, end };
  }
  return null;
}

const STOPWORDS = new Set([
  'the', 'and', 'for', 'that', 'with', 'this', 'from', 'are', 'was', 'were', 'has', 'have', 'had',
  'not', 'but', 'its', 'our', 'their', 'they', 'you', 'your', 'any', 'all', 'can', 'may', 'shall',
  'will', 'would', 'should', 'into', 'under', 'upon', 'than', 'then', 'there', 'which', 'who', 'whom',
  'what', 'when', 'where', 'been', 'being', 'such', 'also',
]);

/** A query's content words (≥3 letters, no stopwords) — what Firm Brain highlights. */
export function queryTerms(query: string): string[] {
  const seen = new Set<string>();
  for (const w of query.toLowerCase().split(/[^\p{L}\p{N}]+/u)) {
    if (w.length >= 3 && !STOPWORDS.has(w)) seen.add(w);
  }
  return [...seen];
}

/** Splits `text` into plain and matched runs for the given terms (case-insensitive, word-prefix). */
export function splitOnTerms(text: string, terms: string[]): { text: string; hit: boolean }[] {
  if (terms.length === 0 || !text) return [{ text, hit: false }];
  const pattern = new RegExp(`(?<![\\p{L}\\p{N}])(${terms.map(escapeRegExp).join('|')})[\\p{L}\\p{N}]*`, 'giu');
  const out: { text: string; hit: boolean }[] = [];
  let last = 0;
  for (const m of text.matchAll(pattern)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ text: text.slice(last, at), hit: false });
    out.push({ text: m[0], hit: true });
    last = at + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), hit: false });
  return out;
}
